import type { ParametrosDeLeitura } from '../../api/configuracao-matching.js';

/**
 * Os parâmetros da leitura por imagem (qr-code.md §9.7) como o formulário os
 * mostra e confere.
 *
 * As faixas são as mesmas que o `PUT /matching-qr/configuracao` valida
 * (§6.1). Conferir aqui é conforto — a pessoa vê o motivo ao lado do campo, e
 * não um 400 depois de clicar —, não segurança: a API recusa de qualquer
 * forma.
 */

export type ChaveDoParametro = keyof ParametrosDeLeitura;

/** O que o formulário guarda enquanto a pessoa digita: tudo texto. */
export type RascunhoDosParametros = Readonly<Record<ChaveDoParametro, string>>;

export type ErrosDosParametros = Readonly<Partial<Record<ChaveDoParametro, string>>>;

export type ResultadoDaValidacao =
  | { readonly valido: true; readonly parametros: ParametrosDeLeitura }
  | { readonly valido: false; readonly erros: ErrosDosParametros };

type Faixa = {
  readonly minimo: number;
  readonly maximo: number;
  readonly inteiro: boolean;
  /** Mínimo exclusivo: a margem zero aceitaria o empate entre dois candidatos. */
  readonly minimoExclusivo?: boolean;
};

const SCORE: Faixa = { minimo: 0, maximo: 1, inteiro: false };

export const FAIXAS: Readonly<Record<ChaveDoParametro, Faixa>> = {
  fallbackTimeoutMs: { minimo: 1000, maximo: 10000, inteiro: true },
  warpSize: { minimo: 400, maximo: 1200, inteiro: true },
  minPxPorModulo: { minimo: 1, maximo: 6, inteiro: false },
  scoreMinimo: SCORE,
  scoreFaixaMedia: SCORE,
  scoreAceite: SCORE,
  margemAceite: { minimo: 0, maximo: 1, inteiro: false, minimoExclusivo: true },
  maxCandidatosConfirmacao: { minimo: 3, maximo: 5, inteiro: true },
};

/** A ordem em que os campos aparecem, e a explicação curta de cada um. */
export const PARAMETROS_DO_FORMULARIO: readonly {
  readonly chave: ChaveDoParametro;
  readonly rotulo: string;
  readonly ajuda: string;
}[] = [
  {
    chave: 'fallbackTimeoutMs',
    rotulo: 'Espera até oferecer a foto (ms)',
    ajuda: 'Tempo sem leitura até o coletor oferecer a foto. De 1.000 a 10.000; padrão 2.500.',
  },
  {
    chave: 'warpSize',
    rotulo: 'Tamanho da imagem endireitada (px)',
    ajuda: 'Lado da imagem do QR depois de corrigida a perspectiva. De 400 a 1.200; padrão 640.',
  },
  {
    chave: 'minPxPorModulo',
    rotulo: 'Nitidez mínima (px por módulo)',
    ajuda:
      'Abaixo disso a foto está borrada ou longe demais, e o coletor pede outra. De 1 a 6; padrão 2.',
  },
  {
    chave: 'scoreMinimo',
    rotulo: 'Semelhança mínima (confiança Baixa)',
    ajuda: 'Abaixo disso o produto nem aparece como candidato. De 0 a 1; padrão 0,45.',
  },
  {
    chave: 'scoreFaixaMedia',
    rotulo: 'Confiança Média a partir de',
    ajuda: 'Entre a semelhança mínima e o aceite automático; padrão 0,55.',
  },
  {
    chave: 'scoreAceite',
    rotulo: 'Aceite automático a partir de',
    ajuda: 'Confiança Alta: o coletor pode aceitar sem perguntar. De 0 a 1; padrão 0,65.',
  },
  {
    chave: 'margemAceite',
    rotulo: 'Vantagem mínima sobre o segundo',
    ajuda:
      'Quanto o primeiro colocado precisa ficar à frente do segundo para o aceite automático. Maior que 0, até 1; padrão 0,15.',
  },
  {
    chave: 'maxCandidatosConfirmacao',
    rotulo: 'Candidatos na confirmação',
    ajuda:
      'Quantos produtos, no máximo, o coletor mostra para o operador escolher. De 3 a 5; padrão 5.',
  },
];

const NUMERO = /^-?\d+(?:[.,]\d+)?$/u;
const formatoBr = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 6 });

function escrever(valor: number, faixa: Faixa): string {
  return faixa.inteiro ? String(valor) : String(valor).replace('.', ',');
}

export function rascunhoDosParametros(parametros: ParametrosDeLeitura): RascunhoDosParametros {
  return {
    fallbackTimeoutMs: escrever(parametros.fallbackTimeoutMs, FAIXAS.fallbackTimeoutMs),
    warpSize: escrever(parametros.warpSize, FAIXAS.warpSize),
    minPxPorModulo: escrever(parametros.minPxPorModulo, FAIXAS.minPxPorModulo),
    scoreMinimo: escrever(parametros.scoreMinimo, FAIXAS.scoreMinimo),
    scoreFaixaMedia: escrever(parametros.scoreFaixaMedia, FAIXAS.scoreFaixaMedia),
    scoreAceite: escrever(parametros.scoreAceite, FAIXAS.scoreAceite),
    margemAceite: escrever(parametros.margemAceite, FAIXAS.margemAceite),
    maxCandidatosConfirmacao: escrever(
      parametros.maxCandidatosConfirmacao,
      FAIXAS.maxCandidatosConfirmacao,
    ),
  };
}

function textoDaFaixa(faixa: Faixa): string {
  const minimo = formatoBr.format(faixa.minimo);
  const maximo = formatoBr.format(faixa.maximo);
  return faixa.minimoExclusivo === true
    ? `Informe um valor maior que ${minimo}, até ${maximo}.`
    : `Informe um valor entre ${minimo} e ${maximo}.`;
}

/** O número do campo, ou o motivo de não ser um número aceito. */
function lerCampo(texto: string, faixa: Faixa): number | string {
  const limpo = texto.trim();

  if (limpo === '') {
    return 'Informe um valor.';
  }
  if (!NUMERO.test(limpo)) {
    return 'Informe um número.';
  }

  const valor = Number(limpo.replace(',', '.'));

  if (faixa.inteiro && !Number.isInteger(valor)) {
    return `Informe um número inteiro. ${textoDaFaixa(faixa)}`;
  }

  const abaixo = faixa.minimoExclusivo === true ? valor <= faixa.minimo : valor < faixa.minimo;
  if (abaixo || valor > faixa.maximo) {
    return textoDaFaixa(faixa);
  }

  return valor;
}

/**
 * Confere cada campo na faixa e os três limiares em ordem: mínimo ≤ média ≤
 * aceite. Fora dessa ordem, um candidato "Médio" teria score menor que um
 * "Baixo" na tela de confirmação (§9.8).
 */
export function validarParametros(rascunho: RascunhoDosParametros): ResultadoDaValidacao {
  const erros: Partial<Record<ChaveDoParametro, string>> = {};
  const lidos: Partial<Record<ChaveDoParametro, number>> = {};

  for (const { chave } of PARAMETROS_DO_FORMULARIO) {
    const lido = lerCampo(rascunho[chave], FAIXAS[chave]);
    if (typeof lido === 'string') {
      erros[chave] = lido;
    } else {
      lidos[chave] = lido;
    }
  }

  const { scoreMinimo, scoreFaixaMedia, scoreAceite } = lidos;

  if (scoreMinimo !== undefined && scoreFaixaMedia !== undefined && scoreFaixaMedia < scoreMinimo) {
    erros.scoreFaixaMedia = 'A confiança Média não pode começar abaixo da semelhança mínima.';
  }
  if (scoreFaixaMedia !== undefined && scoreAceite !== undefined && scoreAceite < scoreFaixaMedia) {
    erros.scoreAceite = 'O aceite automático não pode começar abaixo da confiança Média.';
  }

  if (Object.keys(erros).length > 0) {
    return { valido: false, erros };
  }

  return { valido: true, parametros: lidos as ParametrosDeLeitura };
}
